// =============================================================================
// The Pro entitlement expiry rule — one definition, #362.
//
// `tier === 'pro' && (expires_at is null || expires_at > now)` was copy-pasted
// inline at seventeen gating sites with no shared helper. All seventeen were
// CORRECT (verified 2026-08-20), so this is a refactor, not a fix. The risk it
// removes is drift: a future correction lands in one copy and misses sixteen,
// and the two negative-form variants make a grep-based sweep easy to get wrong.
//
// ⚠️ This decides a PAID BOUNDARY. Two rules follow from that:
//
//   1. It FAILS CLOSED. A missing entitlement, a missing tier, or an
//      unparseable expires_at all return false. Giving Pro away by accident is
//      the expensive direction, and an exception here is not recoverable by a
//      later check — the caller has already been let through.
//
//   2. `now` is injectable so the exact boundary can be asserted. Every call
//      site passes nothing and gets real time; only tests pass a fixed clock.
//
// ⚠️ Boundary semantics, preserved from the sites this replaces: an entitlement
// whose expires_at is EXACTLY now is NOT active. The eight active-form copies
// wrote `expires_at > now` (false at equality) and the two expired-form copies
// wrote `expires_at <= now → false` (true at equality, so it returns false).
// Both agree, and this keeps that.
//
// Lives in _shared/ because that convention now exists and is load-bearing:
// 23 functions import from here, and scripts/expandChangedFunctions.sh (#811,
// 2026-08-10) expands `_shared` into every importing function at deploy time.
// The comment in entitlementGate.ts explaining why four byte-identical copies
// were once justified describes a constraint that no longer applies.
// =============================================================================

/** The shape read from `entitlements`. Both runtimes select tier + expires_at. */
export type EntitlementLike =
  | { tier?: string | null; expires_at?: string | null }
  | null
  | undefined;

/**
 * True when this entitlement grants Pro right now.
 *
 * @param entitlement row from `entitlements`, or null/undefined if none/unread
 * @param now injectable clock; defaults to real time
 */
export function isEntitlementActive(entitlement: EntitlementLike, now: Date = new Date()): boolean {
  // ⚠️ Fail closed. A null row is "we have no evidence she pays", which is not
  // the same as "she does not pay" — but on a paid boundary they get the same
  // answer. See _shared/entitlementGate.ts (#685) for telling those two apart
  // in logging WITHOUT opening the gate.
  if (!entitlement) return false;
  if (entitlement.tier !== "pro") return false;

  // A null/undefined expires_at means a non-expiring entitlement, not an
  // expired one. This is the founding-member and comped case.
  if (entitlement.expires_at == null) return true;

  const expiresAt = new Date(entitlement.expires_at).getTime();
  // ⚠️ NaN from an unparseable value must NOT read as "active". A bare
  // `expiresAt > now` would already be false for NaN, but stating it defends
  // the intent against someone later inverting the comparison.
  if (Number.isNaN(expiresAt)) return false;

  return expiresAt > now.getTime();
}
